import { AlertTriangle, Images, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { useI18n } from '@/lib/i18n';
import type { Pokrycie } from '@/lib/items-api';
import { plikiNaMaszynach, podpowiedzZrodla } from '@/lib/zrodla-obrazka';
import { cn } from '@/lib/utils';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  Z KTÓREGO AUTOMATU ZASILIĆ GALERIĘ.
 *
 *  ⚠️⚠️ POWSTAŁO Z AWARII NA PESIE, 07.09.2026: „maszyna, z której chciało pobrać, nie miała
 *  żadnych obrazków, a pozostałe automaty miały i musi być wybór z jakiego ciągniemy wszystko".
 *  Instancja brała pierwszy węzeł alfabetycznie i nie pytała, czy ten ma cokolwiek w katalogu,
 *  więc zgrywanie kończyło się sukcesem na zero plików.
 *
 *  ⚠️⚠️ TO NIE JEST SAMA LISTA MASZYN — TO LISTA Z POMIAREM. Sam wybór niczego by nie naprawił:
 *  patrząc na trzy nazwy automatów nie da się wiedzieć, który ma bibliotekę. Dlatego przy każdej
 *  pozycji stoi liczba ZMIERZONYCH plików, policzona z tego samego spisu, który wypełnia tabelę
 *  pokrycia — czyli z odczytu katalogów na maszynach, nie z domysłu.
 *
 *  ⚠️⚠️ „NIE SPRAWDZANO" NIE JEST ZEREM i to jest najważniejsze rozróżnienie w tym oknie. Maszyna
 *  bez spisu ma zero ZMIERZONYCH plików, ale to znaczy „nie wiemy", a nie „pusta". Pokazanie tam
 *  zera odesłałoby człowieka od jedynego automatu z pełną biblioteką — dokładnie ten sam błąd,
 *  tylko popełniony na ekranie zamiast w instancji.
 *
 *  ⚠️ WYBÓR MOŻNA ZMIENIĆ NA COŚ SŁABEGO I NIE BLOKUJEMY TEGO. Automat bez zmierzonych plików
 *  wolno wskazać — po świeżym spisie liczby mogą być nieaktualne, a człowiek przy maszynie wie
 *  więcej niż nasz ostatni pomiar. Mówimy tylko, co widzimy.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function ZgrajZAutomatu({
  dane,
  pracuje,
  onZgraj,
  onClose,
}: {
  dane: Pokrycie;
  pracuje: boolean;
  onZgraj: (agentId: string) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const maszyny = plikiNaMaszynach(dane);
  const podpowiedz = podpowiedzZrodla(maszyny);
  const [wybrany, setWybrany] = useState<string | null>(podpowiedz?.agentId ?? null);
  const wybor = maszyny.find((m) => m.agentId === wybrany) ?? null;
  /** ⚠️ Nikt nie ma spisu — wtedy KAŻDA liczba w tym oknie jest zerem z niewiedzy, nie z pomiaru. */
  const nikogoNieMierzono = maszyny.every((m) => m.spisAt === null);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent title={t('obraz.skad.tytul')}>
        <ul className="space-y-1">
          {maszyny.map((m) => {
            const zaznaczony = m.agentId === wybrany;
            return (
              <li key={m.agentId}>
                <label
                  className={cn(
                    'flex cursor-pointer items-start gap-2 rounded-md border p-2 text-sm',
                    zaznaczony ? 'border-[var(--primary)] bg-accent/40' : 'border-border hover:bg-accent/20',
                  )}
                >
                  <input
                    type="radio"
                    name="zrodlo-obrazkow"
                    className="mt-1"
                    checked={zaznaczony}
                    onChange={() => setWybrany(m.agentId)}
                  />
                  <span className="min-w-0">
                    <span className="font-medium">{m.nazwa}</span>
                    {podpowiedz?.agentId === m.agentId && m.ma > 0 && (
                      <span className="ml-2 rounded bg-[var(--primary)]/10 px-1.5 py-0.5 text-[10px] text-[var(--primary)]">
                        {t('obraz.skad.najwiecej')}
                      </span>
                    )}
                    <span className="block text-xs text-muted-foreground">
                      {m.spisAt === null
                        ? t('obraz.skad.nieSprawdzano')
                        : t('obraz.skad.pomiar')
                            .replace('{ma}', String(m.ma))
                            .replace('{brak}', String(m.brak))
                            .replace('{kiedy}', new Date(m.spisAt).toLocaleString())}
                    </span>
                    {/*
                      ⚠️ NIEZMIERZONE POKAZUJEMY OSOBNO, gdy spis był, ale czegoś nie objął (doszły
                      towary po pomiarze). Bez tego liczba „ma" wyglądałaby na pełny obraz katalogu.
                    */}
                    {m.spisAt !== null && m.niezmierzone > 0 && (
                      <span className="block text-xs text-muted-foreground/70">
                        {t('obraz.skad.niezmierzone').replace('{n}', String(m.niezmierzone))}
                      </span>
                    )}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>

        {/*
          ⚠️⚠️ OSTRZEŻENIE ZAMIAST BLOKADY. Zgranie z automatu bez zmierzonych plików prawdopodobnie
          nie przyniesie nic — ale to „prawdopodobnie" opiera się na NASZYM pomiarze, który może być
          starszy niż stan maszyny. Człowiek ma prawo wskazać inaczej, byle wiedział, co widzimy.
        */}
        {nikogoNieMierzono ? (
          <p className="flex items-start gap-1.5 text-xs text-[var(--destructive)]">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            {t('obraz.skad.niktNieMierzony')}
          </p>
        ) : (
          wybor !== null &&
          wybor.ma === 0 && (
            <p className="flex items-start gap-1.5 text-xs text-[var(--destructive)]">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              {wybor.spisAt === null ? t('obraz.skad.wybranyBezSpisu') : t('obraz.skad.wybranyPusty')}
            </p>
          )
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={wybrany === null || pracuje} onClick={() => wybrany !== null && onZgraj(wybrany)}>
            {pracuje ? <Loader2 className="size-4 animate-spin" /> : <Images className="size-4" />}
            {t('obraz.skad.zgraj')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
